import { Box, Button, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ColoredIcon, useStatusColor } from '../ColoredIconAndChip';

const FALLBACK_COMPANY_NAME = 'your provider';

type SubmissionCompleteProps = Readonly<{
  /** The provider that invited the customer. Reads "your provider" when it is not known. */
  companyName?: string;
  /** Where the invite sends the customer back to. Without it the customer is asked to close the tab. */
  returnToCompanyUrl?: string;
  /**
   * Leave out the line that says what to do next, and the return button. For when the caller does
   * not know whether the invite carries a return link (its lookup failed): telling the customer to
   * close the tab, or to return, would then be a guess.
   */
  omitNextStep?: boolean;
}>;

/** What the customer can do now: return to the provider if the invite allows it, else close the tab. */
const NextStep = ({
  provider,
  returnToCompanyUrl,
}: Readonly<{ provider: string; returnToCompanyUrl?: string }>) => {
  if (!returnToCompanyUrl) {
    return (
      <Typography variant="h5" component="p" textAlign="center">
        You can now safely close this tab.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="h5" component="p" textAlign="center">
        {`You can now return to ${provider}`}
      </Typography>
      <Button
        href={returnToCompanyUrl}
        variant="contained"
        data-action="return-to-company"
        sx={{ width: ['100%', 'auto'] }}
      >
        {`Return to ${provider}`}
      </Button>
    </>
  );
};

/**
 * The end screen once the customer has submitted their information (#2354).
 *
 * It thanks the customer and then does one of two things, chosen by whether the invite carries a
 * return link: offers a button to the provider, which the customer clicks, or asks them to close the
 * tab. With `omitNextStep` it does neither. It never counts down and never navigates on its own: an
 * unrequested redirect would move a customer off the page before they had read it (WCAG 2.2.1,
 * 3.2.5).
 */
const SubmissionCompleteComponent = ({
  companyName,
  returnToCompanyUrl,
  omitNextStep = false,
}: SubmissionCompleteProps) => {
  const successColor = useStatusColor('success');
  const provider = companyName?.trim() || FALLBACK_COMPANY_NAME;

  return (
    <Stack
      gap={(theme: Theme) => theme.semantic.spacing.base}
      alignItems="center"
      justifyContent="center"
    >
      {/* ColoredIcon spreads `sx` as an object, so the size comes from the parent's font size. */}
      <Box sx={(theme: Theme) => ({ display: 'flex', fontSize: theme.semantic.fontSize['4xl'] })}>
        <ColoredIcon iconName="checkCircle" iconSize="inherit" statusColor={successColor} />
      </Box>

      <Typography variant="h3" textAlign="center">
        Your Information Has Been Successfully Submitted
      </Typography>

      <Typography variant="h5" component="p" textAlign="center">
        Your service provider will be in touch with you regarding next steps.
      </Typography>

      {!omitNextStep && <NextStep provider={provider} returnToCompanyUrl={returnToCompanyUrl} />}
    </Stack>
  );
};

export const SubmissionComplete = withClarityTheme(SubmissionCompleteComponent);
